/**
 * Per-customer agent start-screen cover images (#1294 follow-up).
 *
 * The source is the customer's Brand assets folder (#1382): whatever is in
 * its `backgrounds/` comes back from GET /brand/:customer and becomes the
 * cover set, via `coversFromBrand`. The hand-maintained catalogue below is
 * the fallback for a customer with no folder yet, and what the page shows
 * while the brand is still loading. A folder cover keeps the catalogue's
 * slug when its file has the same name, so a hearted cover survives the
 * move.
 *
 * `kind` and `description` are laid down now so the planned "about this
 * image" info affordance and per-image favouriting have data to read
 * without another catalogue shape change.
 */
export interface BrandCover {
  slug: string
  url: string
  title: string
  description: string
  kind: 'sport' | 'entertainment' | 'brand'
}

interface CoverDefinition {
  slug: string
  title: string
  description: string
  kind: BrandCover['kind']
}

const BPRSKY_COVERS: CoverDefinition[] = [
  { slug: 'company-template', title: 'Sky', description: "Sky's company cover image.", kind: 'brand' },
  { slug: 'lions-tour-2025', title: 'Lions Tour 2025', description: "The British & Irish Lions' 2025 tour, live on Sky Sports.", kind: 'sport' },
  { slug: 'the-wild-robot', title: 'The Wild Robot', description: "DreamWorks' The Wild Robot, on Sky Cinema.", kind: 'entertainment' },
  { slug: 'saturday-night-live-uk', title: 'Saturday Night Live UK', description: "Sky's UK edition of Saturday Night Live.", kind: 'entertainment' },
  { slug: 'how-to-train-your-dragon', title: 'How to Train Your Dragon', description: 'The live-action How to Train Your Dragon, on Sky Cinema.', kind: 'entertainment' },
  { slug: 'minecraft', title: 'A Minecraft Movie', description: 'A Minecraft Movie, on Sky Cinema.', kind: 'entertainment' },
  { slug: 'us-womens-open', title: "US Women's Open", description: "The US Women's Open golf, live on Sky Sports.", kind: 'sport' },
  { slug: 'nba-knicks', title: 'NBA: New York Knicks', description: 'NBA basketball, live on Sky Sports.', kind: 'sport' },
  { slug: 'icc-womens-t20-world-cup', title: "ICC Women's T20 World Cup", description: "The ICC Women's T20 World Cup, live on Sky Sports.", kind: 'sport' },
  { slug: 'formula-1-2025-norris', title: 'Formula 1 2025: Lando Norris', description: 'Formula 1, live on Sky Sports F1.', kind: 'sport' },
  { slug: 'wsl-man-city', title: 'WSL: Manchester City', description: 'The Women’s Super League, live on Sky Sports.', kind: 'sport' },
  { slug: 'premier-league-arsenal', title: 'Premier League: Arsenal', description: 'The Premier League, live on Sky Sports.', kind: 'sport' },
]

const CUSTOMER_START_COVERS: Record<string, CoverDefinition[]> = {
  bprsky: BPRSKY_COVERS,
}

function coverUrl(customer: string, slug: string): string {
  return `/brand/${customer}/covers/${slug}.jpg`
}

const FAVOURITE_KEY_PREFIX = 'platinum.agentStartCover.'

/**
 * The cover the user hearted on the start screen, per customer, kept in this
 * browser (12 Sep call, #1347 item 16: "set this cover as my start image").
 * `null` when none is set or storage is unavailable.
 */
export function favouriteCoverSlug(customer: string): string | null {
  try {
    return window.localStorage.getItem(FAVOURITE_KEY_PREFIX + customer)
  } catch {
    return null
  }
}

/** Set (or with `null`, clear) the hearted cover for a customer. Best-effort. */
export function setFavouriteCoverSlug(customer: string, slug: string | null): void {
  try {
    if (slug === null) window.localStorage.removeItem(FAVOURITE_KEY_PREFIX + customer)
    else window.localStorage.setItem(FAVOURITE_KEY_PREFIX + customer, slug)
  } catch {
    // Storage blocked: the heart still works for this page view, it just
    // will not stick.
  }
}

/**
 * Which cover the start screen opens on: the hearted one when the catalogue
 * still has it, otherwise one at random (12 Sep call, #1347 item 16 — Matt:
 * "so that we don't always see big fat face every time we log on"; before
 * that the company cover greeted you every time). The refresh button walks
 * on from there in catalogue order. `random` is injectable for tests.
 */
export function initialCoverIndex(
  covers: BrandCover[],
  favouriteSlug: string | null = null,
  random: () => number = Math.random,
): number {
  if (covers.length === 0) return 0
  const favouriteIndex = favouriteSlug ? covers.findIndex(c => c.slug === favouriteSlug) : -1
  if (favouriteIndex >= 0) return favouriteIndex
  return Math.min(covers.length - 1, Math.max(0, Math.floor(random() * covers.length)))
}

/** The customer's catalogue of start-screen cover images, or `[]` when it has none. */
export function customerStartCovers(customer: string | undefined): BrandCover[] {
  if (!customer) return []
  const defs = CUSTOMER_START_COVERS[customer]
  if (!defs) return []
  return defs.map(def => ({ ...def, url: coverUrl(customer, def.slug) }))
}

/** A cover as GET /brand/:customer returns it. */
export interface BrandApiCover {
  file: string
  url: string
  title?: string
  description?: string
  kind?: string
}

const KINDS: ReadonlySet<BrandCover['kind']> = new Set(['sport', 'entertainment', 'brand'])

/** The folder's covers as the start screen uses them: the slug is the file's name, so it matches the catalogue's. */
export function coversFromBrand(covers: BrandApiCover[] | undefined): BrandCover[] {
  if (!covers) return []
  return covers
    .filter(c => c && typeof c.file === 'string' && typeof c.url === 'string' && c.url)
    .map(c => {
      const base = c.file.split('/').pop() ?? c.file
      const slug = base.replace(/\.[^.]+$/, '')
      const kind = KINDS.has(c.kind as BrandCover['kind']) ? (c.kind as BrandCover['kind']) : 'brand'
      return { slug, url: c.url, title: c.title || slug, description: c.description || '', kind }
    })
}
